import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { SelfUserStore } from '@domains/person/user/self/data';
import type { Guid } from './entities/academy-enums.model';
import {
  LiveChatRow,
  LiveParticipantRow,
  LiveQuestionRow,
  LiveReactionRow,
  PendingMute,
  REACTION_EMOJI,
  REACTION_ORDER,
} from './entities/academy-live-panel.model';
import { formatClock } from '@domains/academy/util';
import { AcademyRealtimeStore } from './academy-realtime.store';
import { AcademyQnaChatStore } from './academy-qna-chat.store';
import { AcademyLiveEngagementStore } from './academy-live-engagement.store';

interface AcademyLivePanelState {
  /** The live event currently focused by the panel — drives every computed. */
  eventId: Guid;
  /**
   * The producer studio is the focused view: only then are the producer backlogs (every
   * tenant's questions and chat) read. A viewer page never shows them, even when a producer
   * read of the same live lands late, after the studio was closed.
   */
  producerView: boolean;
  /** Optimistic one-way upvote marks (the REST upvote is idempotent; no un-vote). */
  votedQuestionIds: Set<Guid>;
  /** The studio's own mute/unmute clicks, by person id, until the server confirms them. */
  pendingMutes: Record<Guid, PendingMute>;
}

const initialState: AcademyLivePanelState = {
  eventId: '',
  producerView: false,
  votedQuestionIds: new Set<Guid>(),
  pendingMutes: {},
};

/**
 * Aggregator for the shared live panel (Q&A · Chat · Reactions · Participants).
 *
 * Merges the two raw wire feeds for the focused event — the camelCase SignalR hub
 * (`AcademyRealtimeStore`) and the PascalCase REST reads (`AcademyQnaChatStore` +
 * `AcademyLiveEngagementStore`) — and exposes normalised rows
 * (`LiveQuestionRow`/`LiveChatRow`/`LiveReactionRow`/`LiveParticipantRow`) that the
 * producer room and the viewer watch page bind to directly. Every name comes from the server:
 * a single-room live (backend 014) mixes people of every account, and a lookup on the
 * viewer's side only ever saw the viewer's own.
 *
 * Owns the optimistic vote/mute marks and routes the moderation/engagement intents
 * (`vote`/`toggleMute`) to the real Q&A store. The producer room reads its own REST
 * backlogs (every tenant, with the account names, plus who is muted); the viewer page reads
 * the live's questions and chat history as the server scopes them for a viewer.
 */
export const AcademyLivePanelStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyLivePanelState>(initialState),
  withComputed(
    (store, realtime = inject(AcademyRealtimeStore), qna = inject(AcademyQnaChatStore)) => ({
      /**
       * Who is muted in the chat, as the studio shows it — empty on a viewer page, which has no
       * way to know. Three layers, each over the previous one:
       * - the muted list the studio read (`loadMuted`);
       * - the hub's changes since that read (another producer muting or unmuting);
       * - this studio's own clicks not yet confirmed (see `PendingMute`).
       * Against a service older than round 24 the first two are empty and the clicks are all
       * there is, which is what the studio showed before.
       */
      mutedPersonIds: computed<Set<Guid>>(() => {
        const muted = new Set<Guid>();
        if (!store.producerView()) return muted;
        const id = store.eventId();
        const read = qna.mutedByEvent()[id];
        for (const row of read ?? []) muted.add(row.PersonId);
        const changes = realtime.muteChangesByEvent()[id] ?? {};
        for (const [personId, change] of Object.entries(changes)) {
          if (change.isMuted) muted.add(personId);
          else muted.delete(personId);
        }
        for (const [personId, pending] of Object.entries(store.pendingMutes())) {
          const confirmed = changes[personId];
          if (pending.overRead !== read) continue;
          if (confirmed && confirmed.order > pending.afterChange) continue;
          if (pending.isMuted) muted.add(personId);
          else muted.delete(personId);
        }
        return muted;
      }),
    }),
  ),
  withComputed(
    (
      store,
      realtime = inject(AcademyRealtimeStore),
      qna = inject(AcademyQnaChatStore),
      engagement = inject(AcademyLiveEngagementStore),
      selfUser = inject(SelfUserStore),
    ) => ({
      /**
       * Questions = REST backlog + live hub feed, deduped by id. Dismissed questions disappear.
       * Two REST buffers, one per page, each read only in its own view:
       * - producer studio (`loadProducerQuestions`): every tenant's questions, with the asker's
       *   account name;
       * - viewer (`loadQuestions`): the live's questions as the server scopes them for a viewer
       *   — every account's on a single-room live, the viewer's own account on a live from
       *   before backend 014 — never with an account. Nothing clears it when the watch page
       *   closes, so the studio never reads it: a snapshot taken on a watch page opened from
       *   the studio would override the producer read by id.
       * The asker's name is on both reads and on the hub feed. The viewer's own question, just
       * echoed by `POST /LiveQuestions`, has none until its hub copy arrives.
       * In the studio no row is "mine", the hub feed included. On a viewer page, `Mine` is
       * authorship: the row's `PersonId` is the session user's (`SelfUserStore.personDataAsync`,
       * the same token identity the backend writes on `POST /LiveQuestions`).
       * Votes/status of a hub copy: on a viewer page the hub copy wins over the REST row. In the
       * studio a question already in the producer read keeps the read's votes/status — the studio
       * re-reads after a reconnect, while the hub copy still holds what it had before the drop,
       * and every vote/status the hub delivers is also recorded below.
       * The hub's recorded vote/status values are applied last, so moderation and votes reach
       * REST-loaded rows too. A producer re-join drops the values recorded before it (the
       * re-read replaces them); one the hub delivers after the re-join still wins.
       */
      questions: computed<LiveQuestionRow[]>(() => {
        const id = store.eventId();
        const voted = store.votedQuestionIds();
        const selfPersonId = selfUser.personDataAsync()?.PersonId ?? null;
        const byId = new Map<Guid, LiveQuestionRow>();
        const producerRows = store.producerView() ? (qna.producerQuestionsByEvent()[id] ?? []) : [];
        for (const q of producerRows) {
          byId.set(q.LiveQuestionId, {
            QuestionId: q.LiveQuestionId,
            PersonId: q.PersonId,
            Name: q.PersonName || null,
            TenantName: q.TenantName,
            Content: q.Content,
            UpvoteCount: q.UpvoteCount,
            Status: q.LiveQuestionStatusType,
            Mine: false,
            Voted: voted.has(q.LiveQuestionId),
          });
        }
        const viewerRows = store.producerView() ? [] : (qna.questionsByEvent()[id] ?? []);
        for (const q of viewerRows) {
          byId.set(q.LiveQuestionId, {
            QuestionId: q.LiveQuestionId,
            PersonId: q.PersonId,
            Name: q.PersonName || null,
            TenantName: null,
            Content: q.Content,
            UpvoteCount: q.UpvoteCount,
            Status: q.LiveQuestionStatusType,
            Mine: selfPersonId !== null && q.PersonId === selfPersonId,
            Voted: voted.has(q.LiveQuestionId),
          });
        }
        for (const q of realtime.questionsByEvent()[id] ?? []) {
          const known = byId.get(q.questionId);
          const read = store.producerView() ? known : undefined;
          byId.set(q.questionId, {
            QuestionId: q.questionId,
            PersonId: q.personId,
            Name: q.personName || known?.Name || null,
            // Absent from the viewers' copy of the push; the producers' copy carries it.
            TenantName: q.tenantName ?? known?.TenantName ?? null,
            Content: q.content,
            UpvoteCount: read?.UpvoteCount ?? q.upvoteCount,
            Status: read?.Status ?? q.status,
            Mine: !store.producerView() && selfPersonId !== null && q.personId === selfPersonId,
            Voted: voted.has(q.questionId),
          });
        }
        const patches = realtime.questionPatchesByEvent()[id] ?? {};
        return [...byId.values()]
          .map((q) => {
            const patch = patches[q.QuestionId];
            return patch
              ? {
                  ...q,
                  UpvoteCount: patch.upvoteCount ?? q.UpvoteCount,
                  Status: patch.status ?? q.Status,
                }
              : q;
          })
          .filter((q) => q.Status !== 'Dismissed');
      }),

      /**
       * Chat = REST history + live hub messages, deduped by id (a hub echo wins). Two REST
       * buffers, one per page, each read only in its own view:
       * - viewer (`loadHistory`): the chat as the server scopes it for a viewer — every
       *   account's messages on a single-room live, the viewer's own account on a live from
       *   before backend 014 — with the writer's name and never the writer's account. Nothing
       *   clears it when the watch page closes, so the studio never reads it: a message
       *   removed after that read would come back from it, since the producer read skips the
       *   removed row instead of overriding it, and leaving the watch page drops the hub's
       *   recorded removals;
       * - producer studio (`loadProducerHistory`, every tenant): with the writer's account
       *   name. A message removed by moderation comes back flagged (`IsRemoved`, empty text)
       *   and stays out of the list, exactly as a live `ChatMessageRemoved` takes it out of
       *   the hub buffer.
       * Ids removed — reported by the hub or
       * flagged by the producer read — are left out of every source: a REST copy cannot
       * resurface, and a hub copy of a message removed while the connection was down (the studio
       * re-reads after a reconnect) does not stay listed.
       */
      chat: computed<LiveChatRow[]>(() => {
        const id = store.eventId();
        const muted = store.mutedPersonIds();
        const byId = new Map<Guid, Omit<LiveChatRow, 'Muted'>>();
        const viewerChat = store.producerView() ? [] : (qna.chatByEvent()[id] ?? []);
        for (const m of viewerChat) {
          byId.set(m.ChatMessageId, {
            ChatMessageId: m.ChatMessageId,
            PersonId: m.PersonId,
            Name: m.PersonName || null,
            TenantName: null,
            Text: m.Text,
            IsProducer: m.IsProducer,
          });
        }
        const producerChat = store.producerView() ? (qna.producerChatByEvent()[id] ?? []) : [];
        for (const m of producerChat) {
          if (m.IsRemoved) continue;
          byId.set(m.ChatMessageId, {
            ChatMessageId: m.ChatMessageId,
            PersonId: m.PersonId,
            Name: m.PersonName || null,
            TenantName: m.TenantName,
            Text: m.Text,
            IsProducer: m.IsProducer,
          });
        }
        for (const m of realtime.chatByEvent()[id] ?? []) {
          const known = byId.get(m.chatMessageId);
          byId.set(m.chatMessageId, {
            ChatMessageId: m.chatMessageId,
            PersonId: m.personId,
            Name: m.personName || known?.Name || null,
            // Absent from the viewers' copy of the push; the producers' copy carries it.
            TenantName: m.tenantName ?? known?.TenantName ?? null,
            Text: m.text,
            IsProducer: m.isProducer,
          });
        }
        const removed = new Set([
          ...(realtime.removedChatIdsByEvent()[id] ?? []),
          ...producerChat.filter((m) => m.IsRemoved).map((m) => m.ChatMessageId),
        ]);
        return [...byId.values()]
          .filter((m) => !removed.has(m.ChatMessageId))
          .map((m) => ({
            ...m,
            Muted: m.PersonId !== null && muted.has(m.PersonId),
          }));
      }),

      /**
       * All five reactions in design order. Live hub counts when there is traffic,
       * otherwise the REST totals. Moderation hides the zeros (in the panel itself).
       */
      reactions: computed<LiveReactionRow[]>(() => {
        const id = store.eventId();
        const live = realtime.reactionCountsByEvent()[id] ?? {};
        const rest = qna.reactionsByEvent()[id] ?? {};
        const source = Object.keys(live).length > 0 ? live : rest;
        return REACTION_ORDER.map((t) => ({
          Type: t,
          Emoji: REACTION_EMOJI[t],
          Count: source[t] ?? 0,
        }));
      }),

      /**
       * Participants roster — the REST page (`GET /LiveEvents/{id}/attendances`) merged with
       * the live `ParticipantJoined/Left` events by `LiveAttendanceId`, field by field:
       * - `LeftOnUtc`: a session never reopens, so a closed mark from either side wins (an
       *   older `ParticipantJoined` cannot reopen a row a later REST read already closed);
       * - `WatchedSeconds`: the max of both (`ParticipantJoined` carries none, and heartbeat
       *   progress is only as fresh as the last REST read);
       * - `PersonName` and `TenantName`: the first non-empty one; `null` otherwise (never the
       *   id).
       * Then one row per person — an open session wins over a closed one, then the most
       * recent — so the list agrees with the hub's distinct-person viewer count. Live
       * watchers first, then most recent.
       */
      participants: computed<LiveParticipantRow[]>(() => {
        const id = store.eventId();
        const rest = engagement.attendancesByEvent()[id] ?? [];
        const events = realtime.participantsByEvent()[id] ?? {};
        const sessions = new Map<Guid, LiveParticipantRow & { WatchedSeconds: number }>();
        for (const row of rest) {
          sessions.set(row.LiveAttendanceId, {
            PersonId: row.PersonId,
            Name: row.PersonName || null,
            TenantName: row.TenantName,
            Status: row.LeftOnUtc === null ? 'live' : 'left',
            JoinedOnUtc: row.JoinedOnUtc,
            WatchedSeconds: row.WatchedSeconds,
            WatchedLabel: '',
          });
        }
        for (const event of Object.values(events)) {
          const known = sessions.get(event.liveAttendanceId);
          const left = 'leftOnUtc' in event;
          sessions.set(event.liveAttendanceId, {
            PersonId: event.personId,
            Name: known?.Name || event.personName || null,
            TenantName: known?.TenantName || event.tenantName || null,
            Status: known?.Status === 'left' || left ? 'left' : 'live',
            JoinedOnUtc: known?.JoinedOnUtc ?? event.joinedOnUtc,
            WatchedSeconds: Math.max(known?.WatchedSeconds ?? 0, left ? event.watchedSeconds : 0),
            WatchedLabel: '',
          });
        }
        const byPerson = new Map<Guid, LiveParticipantRow & { WatchedSeconds: number }>();
        for (const session of sessions.values()) {
          const prev = byPerson.get(session.PersonId);
          const wins =
            !prev ||
            (session.Status === 'live' && prev.Status !== 'live') ||
            (session.Status === prev.Status && session.JoinedOnUtc > prev.JoinedOnUtc);
          if (wins) byPerson.set(session.PersonId, session);
        }
        return [...byPerson.values()]
          .map(({ WatchedSeconds, ...row }): LiveParticipantRow => ({
            ...row,
            WatchedLabel: formatClock(WatchedSeconds),
          }))
          .sort((a, b) => {
            if ((a.Status === 'live') !== (b.Status === 'live')) {
              return a.Status === 'live' ? -1 : 1;
            }
            return b.JoinedOnUtc.localeCompare(a.JoinedOnUtc);
          });
      }),

      /** Sessions the server reports for the roster (`Total`), which can exceed the loaded page. */
      participantsTotal: computed<number>(
        () => engagement.attendancesTotalByEvent()[store.eventId()] ?? 0,
      ),

      /** Sessions actually loaded from the REST page — below `participantsTotal` when it is cut. */
      participantsShown: computed<number>(
        () => (engagement.attendancesByEvent()[store.eventId()] ?? []).length,
      ),
    }),
  ),
  withMethods((store, qna = inject(AcademyQnaChatStore), realtime = inject(AcademyRealtimeStore)) => ({
    /**
     * Focus a live event, from the producer studio (`producerView`) or a viewer page; resets
     * the optimistic marks when the target changes.
     */
    focus(eventId: Guid, producerView = false): void {
      if (store.eventId() === eventId && store.producerView() === producerView) return;
      patchState(store, {
        eventId,
        producerView,
        votedQuestionIds: new Set<Guid>(),
        pendingMutes: {},
      });
    },

    /** One-way upvote — idempotent and there is no un-vote; marks optimistically. */
    vote(questionId: Guid): void {
      if (store.votedQuestionIds().has(questionId)) return;
      patchState(store, {
        votedQuestionIds: new Set(store.votedQuestionIds()).add(questionId),
      });
      qna.upvote({ liveEventId: store.eventId(), questionId });
    },

    /**
     * Mute / unmute a chat author through the Q&A store, from what the studio shows now — the
     * server's word included, so someone another producer muted is unmuted in one click. The
     * click is marked at once and stands until the server confirms it (`PendingMute`).
     */
    toggleMute(personId: Guid): void {
      const wasMuted = store.mutedPersonIds().has(personId);
      patchState(store, {
        pendingMutes: {
          ...store.pendingMutes(),
          [personId]: {
            isMuted: !wasMuted,
            afterChange: realtime.muteChangeCount(),
            overRead: qna.mutedByEvent()[store.eventId()],
          },
        },
      });
      const payload = { liveEventId: store.eventId(), personId };
      if (wasMuted) qna.unmute(payload);
      else qna.mute(payload);
    },

    /** Clears the focus + optimistic marks (navigation / teardown). */
    reset(): void {
      patchState(store, initialState);
    },
  })),
);
